/**
 * 計助ダッシュボード: データ数（N）ステッパーコントロール。
 * ±ボタン + 数値入力。入力中はローカル下書きのみ更新し、確定時に clamp して親へ渡す
 * （「12」入力中の空／1 で行が潰れるのを防ぐ）。
 */

import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KEISUKE_GROUP_OBSERVATION_N_MAX,
  KEISUKE_GROUP_OBSERVATION_N_MIN,
} from '../../../../stores/systems/keisuke/dashboardStore';
import grid from './KeisukeUnifiedDataGrid.module.scss';

export interface KeisukeDashboardGridObservationCountRowProps {
  label: ReactNode;
  value: number;
  onChange: (n: number) => void;
  onBlur?: () => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  id?: string;
}

function clampObservationCount(raw: string, min: number, max: number): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return Math.min(max, Math.max(min, Math.trunc(n)));
}

export default function KeisukeDashboardGridObservationCountRow({
  label,
  value,
  onChange,
  onBlur,
  disabled = false,
  min = KEISUKE_GROUP_OBSERVATION_N_MIN,
  max = KEISUKE_GROUP_OBSERVATION_N_MAX,
  id: idProp,
}: KeisukeDashboardGridObservationCountRowProps) {
  const { t } = useTranslation();
  const uid = useId();
  const fieldId = idProp ?? uid;
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commitDraft = (raw: string) => {
    const next = clampObservationCount(raw, min, max);
    if (next == null) {
      setDraft(String(value));
      return;
    }
    setDraft(String(next));
    if (next !== value) onChange(next);
  };

  const decrement = () => {
    const next = Math.max(min, value - 1);
    if (next !== value) onChange(next);
  };

  const increment = () => {
    const next = Math.min(max, value + 1);
    if (next !== value) onChange(next);
  };

  return (
    <div className={`${grid.nPerCellRowDense} ${grid.nPerCellRowDenseFlex}`}>
      <label className={grid.nPerCellRowDenseLabel} htmlFor={fieldId}>
        {label}
      </label>
      <div className={grid.keisukeNStepper}>
        <button
          type="button"
          className={grid.keisukeNStepperBtn}
          onClick={decrement}
          disabled={disabled || value <= min}
          tabIndex={-1}
          aria-label={t('keisuke.dashboard.grid.decreaseObservation')}
        >
          −
        </button>
        <input
          id={fieldId}
          name={fieldId}
          type="number"
          min={min}
          max={max}
          className={grid.keisukeNStepperInput}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={(e) => {
            commitDraft(e.currentTarget.value);
            onBlur?.();
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            commitDraft((e.target as HTMLInputElement).value);
            (e.target as HTMLInputElement).blur();
          }}
          disabled={disabled}
        />
        <button
          type="button"
          className={grid.keisukeNStepperBtn}
          onClick={increment}
          disabled={disabled || value >= max}
          tabIndex={-1}
          aria-label={t('keisuke.dashboard.grid.increaseObservation')}
        >
          +
        </button>
      </div>
    </div>
  );
}
